import type { FC } from 'react'

import { OfMassMessagesService } from '@api'
import { differenceInMinutes } from 'date-fns'
import { useUnit } from 'effector-react'
import { useMutation } from 'react-query'

import { useToast } from '@/components/ui/toast'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $hasMassMessagesPermission } from '@/models/permissions.ts'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers'

import { useMessageQueue } from '../../hooks/use-message-queue'
import { MessageQueueItem } from './message-queue-item'

interface Props {}

export const MessageQueueList: FC<Props> = () => {
  const creatorId = useUnit($creatorId)
  const hasMassMessagesPermission = useUnit($hasMassMessagesPermission)
  const toast = useToast()
  const { messagesQueue } = useMessageQueue()

  const filteredData = messagesQueue
    ?.filter((queue) => {
      if (queue.isDone && !queue.isCanceled) {
        return differenceInMinutes(new Date(queue.date), new Date()) < 2
      }

      return queue.total > 0 && queue.isReady && !queue.isCanceled
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  const unsendMutation = useMutation(
    ({ queueId }: { queueId: number }) =>
      OfMassMessagesService.deleteMassMessage({
        creatorId: creatorId!,
        massMessageId: queueId,
      }),
    {
      onSuccess: () => {
        toast.success('Successfully unsent mass message!')
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while unsending mass message')
      },
    },
  )

  if (!hasMassMessagesPermission) {
    return null
  }

  return (
    <div
      className={classNames(
        'flex flex-col gap-2 px-2 text-sm',
        'mb-2 max-h-[120px] overflow-auto',
      )}
    >
      {filteredData?.map((queue) => (
        <MessageQueueItem
          queue={queue}
          key={queue.id}
          onUnsend={(id) => {
            openDialog({
              type: DIALOG_TYPE.CONFIRMATION,
              title: `Please confirm you want to unsend this message from ${queue?.total} chats`,
              onConfirm: () => unsendMutation.mutateAsync({ queueId: id }),
            })
          }}
        />
      ))}
    </div>
  )
}
